var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable react/require-default-props */
import { useId } from '@reach/auto-id';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import { useIsViewportMobile } from '../../../hooks/useIsViewport';
import { media } from '../../../components/themes/theme';
import { cloudinaryUrl } from '../../../components/ImageV2/components/cloudinary';
import { MediaPlayerButton } from '../../../components/AudioPlayer/MediaPlayerButton';
import { useOnScreen } from '../../../hooks/useOnScreen';
import { useMediaPlayer } from '../../../components/AudioPlayer/MediaContext';
export const VideoWrapper = styled.div `
  display: grid;
  grid-template-columns: auto;
  position: relative;
  padding-bottom: 1.5rem;

  ${media.laptop `
    padding-bottom: 1.875rem;
  `};
`;
export const VideoArea = styled.div `
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: ${props => props.heightPercent};
`;
export const Video = styled.video `
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
export const ButtonArea = styled.div `
  bottom: 0;
  height: 3rem;
  position: absolute;
  right: 1.5rem;
  width: 3rem;

  ${media.laptop `
    height: 3.75rem;
    right: 1.875rem;
    width: 3.75rem;
  `};
`;
export const VideoPlayer = (_a) => {
    var { 'data-testid': dataTestId = 'video-player--audioPlayButton', alt, srcDesktop, srcMobile, posterMobile, posterDesktop, crop = 'fill', controls = true } = _a, rest = __rest(_a, ['data-testid', "alt", "srcDesktop", "srcMobile", "posterMobile", "posterDesktop", "crop", "controls"])
    // eslint-disable-next-line sonarjs/cognitive-complexity
    ;
    const isMobile = useIsViewportMobile();
    const [percentPlayed, setPercentPlayed] = useState();
    const [heightPercent, setHeightPercent] = useState('56.25%');
    const videoRef = useRef(null);
    const [poster, setPoster] = useState('');
    const [src, setSrc] = useState('');
    const uniqueId = useId(srcDesktop);
    // Generate unique player ID
    const mediaPlayerId = useMemo(() => uniqueId + src, [uniqueId, src]);
    // Fetch list of all media players
    const { mediaPlayers } = useMediaPlayer();
    // Playback handlers
    const start = useCallback(() => {
        var _a;
        if (((_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.paused) === true) {
            // Stop all other players
            for (const mp of mediaPlayers) {
                mp.stop();
            }
            // Start playback
            videoRef.current.play();
            // Update progress indicator
            setPercentPlayed(0);
        }
    }, [mediaPlayers]);
    const stop = useCallback(() => {
        var _a;
        if (((_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.paused) === false) {
            // Stop playback
            videoRef.current.pause();
            // Update progress indicator
            setPercentPlayed(undefined);
            // Rewind
            videoRef.current.currentTime = 0;
        }
    }, []);
    // Click handler
    const handleClick = useCallback(() => {
        var _a, _b;
        if (((_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.paused) === true) {
            start();
        }
        else if (((_b = videoRef.current) === null || _b === void 0 ? void 0 : _b.paused) === false) {
            stop();
        }
    }, [start, stop]);
    // Update the progress indicator
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (videoRef.current) {
                if (videoRef.current.paused) {
                    setPercentPlayed(undefined);
                }
                else {
                    const p = videoRef.current.currentTime / videoRef.current.duration;
                    setPercentPlayed(p * 100);
                }
            }
        }, 250);
        return () => {
            clearInterval(intervalId);
        };
    }, []);
    // Update video wrapper height from video meta data
    const handleOnLoadedMetadata = useCallback(() => {
        if (videoRef.current) {
            const p = (videoRef.current.videoHeight / videoRef.current.videoWidth) * 100 + '%';
            setHeightPercent(p);
        }
    }, [setHeightPercent, videoRef]);
    // Stop when player is scrolled out of viewport
    const isVisible = useOnScreen({
        ref: videoRef,
        root: null,
        rootMargin: '0px',
        threshold: 0,
    });
    if (!isVisible) {
        stop();
    }
    // MediaPlayer context integration
    useEffect(() => {
        const mp = {
            id: mediaPlayerId,
            start,
            stop,
        };
        mediaPlayers.push(mp);
        return () => {
            mediaPlayers.splice(mediaPlayers.indexOf(mp, 1));
        };
    }, [mediaPlayerId, mediaPlayers, start, stop]);
    // video src
    useEffect(() => {
        if (isMobile) {
            if (src !== srcMobile) {
                setSrc(srcMobile || srcDesktop);
            }
        }
        else {
            if (src !== srcDesktop) {
                setSrc(srcDesktop);
            }
        }
    }, [isMobile, srcMobile, srcDesktop, src]);
    // video poster
    useEffect(() => {
        if (isMobile) {
            if (posterMobile) {
                setPoster(cloudinaryUrl({
                    src: posterMobile,
                    width: 1000,
                    height: 1000,
                    crop,
                }));
            }
            else {
                setPoster(process.env.NEXT_PUBLIC_VIDEO_PLAYER_POST_MOBILE || '');
            }
        }
        else {
            if (posterDesktop) {
                setPoster(cloudinaryUrl({
                    src: posterDesktop,
                    width: 1920,
                    height: 1080,
                    crop,
                }));
            }
            else {
                setPoster(process.env.NEXT_PUBLIC_VIDEO_PLAYER_POST_DESKTOP || '');
            }
        }
    }, [crop, isMobile, posterMobile, posterDesktop]);
    return (React.createElement(VideoWrapper, { "data-testid": dataTestId },
        React.createElement(VideoArea, { heightPercent: heightPercent },
            React.createElement(Video, Object.assign({}, rest, { "data-testid": `${dataTestId}__video`, playsInline: true, ref: videoRef, src: src, poster: rest.autoPlay ? undefined : poster, preload: "metadata", onLoadedMetadata: handleOnLoadedMetadata }), alt)),
        controls !== false && (React.createElement(ButtonArea, null,
            React.createElement(MediaPlayerButton, { "data-testid": `${dataTestId}__media-button`, isBoxed: true, onClick: handleClick, progress: percentPlayed })))));
};
